/**
 * StylizedNumberedListRenderer - Renders the shared numbered and bulleted list block
 */

import React, { useMemo } from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  useTheme,
  type NumberedListItem,
  type StylizedListTitleLevel,
} from '@course-authoring/shared-types';

import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { createStylizedListStyles } from './styles/stylizedList';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { RichTextContent } from './RichTextContent';

export interface StylizedNumberedListRendererProps {
  title?: string;
  titleLevel?: StylizedListTitleLevel;
  items: NumberedListItem[];
  listStyle?: 'numbered' | 'bulleted';
  lessonThemeColor?: string;
  className?: string;
  style?: React.CSSProperties;
}

function hasTitleText(title?: string): boolean {
  return typeof title === 'string' && title.trim().length > 0;
}

function getTitleTag(titleLevel: StylizedListTitleLevel): 'h1' | 'h2' | 'h3' {
  if (titleLevel === 'h1') {
    return 'h1';
  }

  if (titleLevel === 'h2') {
    return 'h2';
  }

  return 'h3';
}

const contentInnerStyles = createRichTextContentResetStyles('snl-content');

export function StylizedNumberedListRenderer({
  title,
  titleLevel,
  items,
  listStyle,
  lessonThemeColor,
  className,
  style,
}: StylizedNumberedListRendererProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const resolvedTitleLevel = titleLevel ?? 'h3';
  const resolvedListStyle = listStyle ?? 'numbered';
  const stylizedListStyles = useMemo(
    () => createStylizedListStyles(tokens, isMobile, isSmallMobile, resolvedTitleLevel, resolvedListStyle, lessonThemeColor),
    [tokens, isMobile, isSmallMobile, resolvedTitleLevel, resolvedListStyle, lessonThemeColor]
  );
  const isBulleted = resolvedListStyle === 'bulleted';
  const hasTitle = hasTitleText(title);
  const ListTag = isBulleted ? 'ul' : 'ol';
  const TitleTag = getTitleTag(resolvedTitleLevel);

  if (!items || items.length === 0) {
    return (
      <div className={className} style={{ ...stylizedListStyles.container, ...style }}>
        {hasTitle && <TitleTag style={stylizedListStyles.title}>{title?.trim()}</TitleTag>}
        <p style={stylizedListStyles.placeholder}>Add items to this list</p>
      </div>
    );
  }

  return (
    <div className={className} style={{ ...stylizedListStyles.container, ...style }}>
      <style>{contentInnerStyles}</style>
      {hasTitle && <TitleTag style={stylizedListStyles.title}>{title?.trim()}</TitleTag>}
      <ListTag style={stylizedListStyles.list}>
        {items.map((item, index) => {
          const html = safeGenerateRichTextHTML(item.content, extensions);

          return (
            <li key={item.id} style={stylizedListStyles.item}>
              {isBulleted ? (
                <span aria-hidden="true" style={stylizedListStyles.bulletMarker} />
              ) : (
                <div aria-hidden="true" style={stylizedListStyles.numberMarker}>
                  {index + 1}
                </div>
              )}
              <RichTextContent
                className="snl-content"
                style={stylizedListStyles.itemContent}
                html={html}
              />
            </li>
          );
        })}
      </ListTag>
    </div>
  );
}

StylizedNumberedListRenderer.displayName = 'StylizedNumberedListRenderer';
